import React, { useRef, useEffect } from 'react';
import classnames from 'classnames';
import { fixTikTokEmbedCode, loadLazyImage } from '@/utils';
import { useRouter } from 'next/router';
import { scrollIntoView } from '@/utils';
import SmoothScroller from './SmoothScroller';

export interface IPageContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  fullPageSlider?: boolean;
}

const PageContainer = ({ className, fullPageSlider, ...props }: IPageContainerProps) => {
  const lazyTimer = useRef<ReturnType<typeof setTimeout>>(null);
  const previousPageUrl = useRef<string | null>(null);
  const router = useRouter();

  useEffect(() => {
    startToLoadLazyImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (window.location.hash) {
      try {
        const element = document.querySelector(window.location.hash) as HTMLElement | null;
        if (element) {
          setTimeout(() => {
            const style = window.getComputedStyle(document.body);
            scrollIntoView(element, parseInt(style.getPropertyValue('--header-height'), 10) * -1);
          }, 300);
        }
      } catch (err) {
        console.log(err);
      }
    }
    if (typeof window !== 'undefined') {
      // Need to store previous page url for HubSpot form
      sessionStorage.setItem('previousPageUrl', previousPageUrl.current || '');
      previousPageUrl.current = window.location.href;

      fixTikTokEmbedCode();
    }
  }, [router.asPath]);

  useEffect(() => {
    if (fullPageSlider) {
      document.documentElement.classList.add('md:scroll-snap-type-y-mandatory');
    } else {
      document.documentElement.classList.remove('md:scroll-snap-type-y-mandatory');
    }
  }, [fullPageSlider]);

  const startToLoadLazyImages = () => {
    if (lazyTimer.current) {
      clearTimeout(lazyTimer.current);
    }
    lazyTimer.current = setTimeout(() => {
      loadLazyImagesChunk();
    }, 2000);
  };

  // load lazy images
  const loadLazyImagesChunk = () => {
    const elList = document.querySelectorAll('.lazy-image:not(.lazy-loaded)') as NodeListOf<HTMLElement> | null;
    if (elList && elList.length > 0) {
      let imgEl: HTMLImageElement | undefined;
      for (let i = 0; i < 5; i++) {
        loadLazyImage(elList[i]);
        if (elList[i] && i === 4) {
          const media =
            elList[i].children && elList[i].children.length > 0 ? [...elList[i].children, elList[i]] : [elList[i]];

          imgEl = media.find((cEl) => cEl.tagName.toLowerCase() === 'img') as HTMLImageElement | undefined;
        }
      }
      if (imgEl) {
        if (imgEl.complete) {
          loadLazyImagesChunk();
        } else {
          imgEl.addEventListener('load', () => {
            loadLazyImagesChunk();
          });
        }
      } else {
        loadLazyImagesChunk();
      }
    } else {
      startToLoadLazyImages();
    }
  };

  return (
    <>
      <div
        className={classnames(
          'page-container will-change-transform',
          fullPageSlider && 'md:[&_.module]:snap-end md:[&_footer]:snap-start',
          className,
        )}
        {...props}
      />
      {!fullPageSlider && <SmoothScroller />}
    </>
  );
};

export default PageContainer;
